import { useEffect, useMemo, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { zonedTimeToUtc } from 'date-fns-tz';
import { Chart as ChartJS, Legend, LinearScale, CategoryScale, PointElement, LineElement, Filler } from 'chart.js';

import { SkeletonChart } from '/src/components/SkeletonChart';
import { useTimeZonedFunctions, useChartTooltip } from '/src/hooks';
import { useSelectedSource } from '/src/components/source-selector/source-selector';
import { TIMEZONES, ROOT_ELEMENT_ID } from '../../_global/constants';
import { ChartTooltipArrow } from '/src/components/ChartTooltipArrow';
import { createPortal } from 'react-dom';

ChartJS.defaults.font.family = 'Proxima Nova, Lucida Grande, Tahoma, Verdana, Arial, sans-serif';
ChartJS.defaults.font.size = 14;

ChartJS.register(Legend, LinearScale, CategoryScale, PointElement, LineElement, Filler);

const tooltipOptions = { forceDirection: 'top' };

export function Chart({
  data = [],
  yScaleCallback = null,
  yScaleStepSize = null,
  metricName = null,
  isLoading,
  suggestedMax = 1000,
  max = null,
  timeUnit = null,
}) {
  const chartRef = useRef(null);
  const [gradient, setGradient] = useState(false);
  const { selectedSource } = useSelectedSource();

  const { formatChartTooltip, formatChartLabel } = useTimeZonedFunctions(
    selectedSource?.currentTimezone ?? TIMEZONES.UTC,
  );

  useEffect(() => {
    const chartInstance = chartRef.current;

    // wait for the chart to be drawn
    const updateGradient = () => {
      if (!chartInstance.chartArea) return;

      const { ctx, chartArea } = chartInstance;
      const { top, bottom } = chartArea;

      const gradient = ctx.createLinearGradient(0, top, 0, bottom);

      gradient.addColorStop(0, 'rgba(168, 92, 208, 0.2)');
      gradient.addColorStop(1, 'rgba(168, 92, 208, 0)');

      setGradient(gradient);
    };

    // call after first render
    if (chartRef.current && !gradient) {
      updateGradient();
    }

    // recalculate gradient on chart resize
    chartInstance.options.animation = {
      onComplete: () => {
        if (chartRef.current && !gradient) {
          updateGradient();
        }
      },
    };
  }, [gradient]);

  const labels = useMemo(() => {
    const labels =
      data?.bucket?.map?.((d) => {
        return formatChartLabel(
          zonedTimeToUtc(`${d.startDate}T00:00:00`, selectedSource?.currentTimezone ?? TIMEZONES.UTC),
          timeUnit,
        );
      }) ?? [];
    // trick ChartJS into centering a single label
    if (labels.length === 1) {
      return ['', labels[0], ''];
    }

    return labels;
  }, [data, formatChartLabel, selectedSource?.currentTimezone, timeUnit]);

  const values = useMemo(() => {
    const vals = data?.bucket?.map?.((d) => d.value) ?? [];

    if (data?.bucket?.length === 1) {
      return [null, vals[0], null];
    }

    return vals;
  }, [data]);

  const maxValue = useMemo(() => (values.length === 0 ? 0 : Math.max(...values)), [values]);
  const chartSuggestedMax = useMemo(
    () => (maxValue === 0 ? suggestedMax : Math.ceil(maxValue * 1.1)),
    [maxValue, suggestedMax],
  );

  const { tooltip, external } = useChartTooltip(tooltipOptions);

  const distributionData = useMemo(() => {
    return {
      labels,
      datasets: [
        {
          data: values,
          borderColor: '#A85CD0',
          backgroundColor: gradient,
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 5,
          pointStyle: 'circle',
          pointBackgroundColor: '#A85CD0',
          clip: false,
          label: metricName,
        },
      ],
    };
  }, [values, labels, gradient, metricName]);

  const options = useMemo(() => {
    return {
      responsive: true,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      maintainAspectRatio: false,
      spanGaps: true,
      scales: {
        y: {
          suggestedMin: 0,
          suggestedMax: chartSuggestedMax,
          max,
          ticks: {
            color: 'rgb(104, 118, 141)',
            stepSize: yScaleStepSize || 1000,
            padding: 10,
            maxTicksLimit: 5,
            callback: yScaleCallback,
          },
          grid: {
            color: 'rgb(225, 230, 239)',
            lineWidth: 1,
          },
          border: {
            display: false,
          },
        },
        // y2: conversionsDataset
        //   ? {
        //     type: 'linear',
        //     position: 'right',
        //     min: 0,
        //     grid: { drawOnChartArea: false },
        //     border: { display: false },
        //     ticks: {
        //       color: 'rgb(104, 118, 141)',
        //       precision: 0,
        //     },
        //     title: {
        //       display: true,
        //       text: 'Conversions',
        //       color: 'rgb(104, 118, 141)',
        //       font: { size: 13, weight: '600' },
        //       padding: { bottom: 8 },
        //     },
        //   }
        //   : undefined,
        x: {
          ticks: {
            autoSkip: labels.length <= 10 ? false : undefined,
            color: 'rgb(104, 118, 141)',
            padding: 15,
            maxTicksLimit: 7,
            callback: function (value, _index, _values) {
              return this.getLabelForValue(value);
            },
          },
          grid: {
            display: false,
          },
        },
      },
      plugins: {
        tooltip: {
          enabled: false,
          position: 'nearest',
          external,
          callbacks: {
            title: (tooltipItems) => {
              const idx = tooltipItems?.[0]?.dataIndex;
              const bucket = data?.bucket?.length === 1 ? data.bucket[0] : data?.bucket?.[idx];
              if (!bucket) return '';
              const timeZone = selectedSource?.currentTimezone ?? TIMEZONES.UTC;
              const startAt = zonedTimeToUtc(`${bucket.startDate}T00:00:00`, timeZone);
              const endAt = zonedTimeToUtc(`${bucket.endDate}T00:00:00`, timeZone);
              return formatChartTooltip(startAt, endAt, timeUnit);
            },
          },
        },
        legend: {
          labels: false,
        },
      },
      layout: {
        padding: {
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
        },
      },
    };
  }, [
    yScaleCallback,
    yScaleStepSize,
    chartSuggestedMax,
    labels,
    max,
    data?.bucket,
    external,
    formatChartTooltip,
    selectedSource?.currentTimezone,
    timeUnit,
  ]);

  if (isLoading) {
    return <SkeletonChart />;
  }

  const bucketIndex = data?.bucket?.length === 1 ? 0 : tooltip?.dataIndex;
  const bucket = data?.bucket?.[bucketIndex];

  return (
    <>
      <Line ref={chartRef} data={distributionData} options={options} className="w-full h-auto" />
      {renderChartTooltip({
        tooltip,
        label: bucket ? formatChartTooltip(bucket.startDate, bucket.endDate, timeUnit) : '',
        valueFormatter: getValueFormatter(bucket, yScaleCallback),
      })}
    </>
  );
}

function renderChartTooltip({ tooltip, label, valueFormatter }) {
  if (!tooltip || !tooltip.visible) return null;
  if (typeof document === 'undefined') return null;
  const root = document.querySelector(`#${ROOT_ELEMENT_ID}`);
  const portalOffset = getPortalOffset(tooltip);
  const content = (
    <ChartTooltip tooltip={tooltip} label={label} valueFormatter={valueFormatter} positionOffset={portalOffset} />
  );

  return root ? createPortal(content, root) : content;
}

function getPortalOffset(tooltip) {
  if (typeof window === 'undefined') return { x: 0, y: 0 };
  const left = tooltip?.canvasLeft ?? 0;
  const top = tooltip?.canvasTop ?? 0;
  return {
    x: left + window.scrollX,
    y: top + window.scrollY,
  };
}

function ChartTooltip({ tooltip, valueFormatter, positionOffset = { x: 0, y: 0 } }) {
  if (!tooltip || !tooltip.visible) return null;
  const { anchorX, anchorY, attachSide, items = [], transform, offsetXMultiplier, offsetYMultiplier } = tooltip;

  const GAP = 12; // distance from tooltip to chart point

  let offsetX = offsetXMultiplier * GAP;
  let offsetY = offsetYMultiplier * GAP;

  return (
    <div
      role="tooltip"
      className="absolute pointer-events-none max-w-[180px] bg-white rounded p-2.5 text-sm border border-mystic-500"
      style={{
        left: positionOffset.x + anchorX + offsetX,
        top: positionOffset.y + anchorY + offsetY,
        transform,
      }}
    >
      {tooltip.label ? <div className="font-semibold mb-1.25">{tooltip.label}</div> : null}

      <div>
        {items?.map((it, idx) => (
          <div key={`${it.datasetIndex}-${it.dataIndex}-${idx}`} className="flex justify-between items-center">
            <div className="flex items-center min-w-0 space-x-2.5 justify-between w-full">
              <span className="truncate !text-xs text-charcoal-gray-500 font-normal mt-px">
                {it.datasetLabel || 'Value'}
              </span>
              <span className="flex-shrink-0 font-semibold" style={{ color: it.color }}>
                {valueFormatter(it.raw)}
              </span>
            </div>
          </div>
        ))}
      </div>

      <ChartTooltipArrow side={attachSide} variant="white" />
    </div>
  );
}

function getValueFormatter(bucket, yScaleCallback) {
  if (yScaleCallback) {
    return (value) => yScaleCallback(value.toLocaleString(), bucket);
  }
  return (value) => value.toLocaleString();
}
